import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as api from '../api';
import { HeaderBack, RNLLoader, Select } from '../components/UI';
import { hasRole } from '../utils';
import MMDashboard from '../components/materialMapping/MMDashboard';
import MMRegister from '../components/materialMapping/MMRegister';
import MMUnmapped from '../components/materialMapping/MMUnmapped';
import MMMapped from '../components/materialMapping/MMMapped';
import MMProjects from '../components/materialMapping/MMProjects';
import MMSiteMeasurement from '../components/materialMapping/MMSiteMeasurement';
import MMEditor from '../components/materialMapping/MMEditor';

// BOQ Hub → Material Mappings (the Base44 "BOQ–Material Mapping" module).
//   /boq-hub/material-mappings/:tab?project=<id>      dashboard | register | unmapped | mapped | projects | site-measurement
//   /boq-hub/material-mappings/map/:boqItemId?project=<id>[&material=<id>]   map one BOQ line to materials (the editor)
const BASE = '/boq-hub/material-mappings';
const TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'register', label: 'Mapping Register' },
  { id: 'unmapped', label: 'Unmapped Materials' },
  { id: 'mapped', label: 'Mapped Materials' },
  { id: 'projects', label: 'MAP BOQs' },
  { id: 'site-measurement', label: 'Site Measurement (Auto)' },
];
// who may create and change mappings (the backend allows the same roles; super admin always)
const WRITERS = ['project_manager', 'quantity_surveyor'];

export default function MaterialMappingPage({ user, toast }) {
  const navigate = useNavigate();
  const { tab = 'dashboard', boqItemId } = useParams();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project') || '';
  const [projects, setProjects] = useState(null);
  const [lists, setLists] = useState({ divisions: [], categories: [], subsections: [] });
  const canEdit = hasRole(user, WRITERS);

  useEffect(() => {
    api.listProjects()
      .then(r => setProjects(Array.isArray(r) ? r : (r?.data || r?.items || [])))
      .catch(() => setProjects([]));
  }, []);

  // buildings, disciplines and work packages of the selected project, for every tab's filters
  useEffect(() => {
    if (!projectId) { setLists({ divisions: [], categories: [], subsections: [] }); return; }
    Promise.all([
      api.listProjectDivisions(projectId).catch(() => []),
      api.listDmrCategories(projectId).catch(() => []),
      api.listDmrSubsections(projectId).catch(() => []),
    ]).then(([d, c, s]) => setLists({
      divisions: Array.isArray(d) ? d : (d?.data || []),
      categories: Array.isArray(c) ? c : (c?.data || []),
      subsections: Array.isArray(s) ? s : (s?.data || []),
    }));
  }, [projectId]);

  const goTo = useCallback((next, query = {}) => {
    api.clearApiCache();
    const q = new URLSearchParams({ ...(projectId ? { project: projectId } : {}), ...query });
    navigate(`${BASE}/${next}${q.toString() ? `?${q}` : ''}`);
  }, [navigate, projectId]);

  const openMapper = useCallback((itemId, query = {}) => goTo(`map/${itemId}`, query), [goTo]);
  const setProject = (id, nextTab = boqItemId ? 'register' : tab) => {
    api.clearApiCache();
    navigate(`${BASE}/${nextTab}${id ? `?project=${id}` : ''}`);
  };

  const project = useMemo(() => (projects || []).find(p => String(p.project_id || p.id) === String(projectId)), [projects, projectId]);
  const inEditor = !!boqItemId;
  const shown = inEditor ? null : (TABS.some(t => t.id === tab) ? tab : 'dashboard');
  const needsProject = shown !== 'projects' && !projectId;

  if (projects === null) return <RNLLoader title="Loading Material Mappings..." subtitle="Fetching projects" />;

  const shared = { projectId, project, lists, goTo, openMapper, canEdit, toast, user };

  return (
    <div className="fade-in" style={{ padding: '0 0 24px', marginTop: -28 }}>
      {!inEditor && <HeaderBack onClick={() => navigate('/boq-hub')}>Back to BOQ Hub</HeaderBack>}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${TABS.length}, minmax(0, 1fr))`, gap: 10, marginBottom: 20, paddingTop: 16 }}>
        {TABS.map(t => {
          const on = shown === t.id;
          return (
            <button key={t.id} onClick={() => goTo(t.id)} style={{
              padding: '18px 10px', background: on ? '#0b318bff' : 'var(--bg-card)', color: on ? '#fff' : 'var(--text-primary)',
              border: '1px solid black', borderRadius: 12, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center',
              boxShadow: on ? '0 4px 12px rgba(11,49,139,0.2)' : 'var(--shadow-sm)', transition: 'all 0.2s ease',
            }}>{t.label}</button>
          );
        })}
      </div>

      <div style={{ background: 'var(--bg-card)', border: '3px solid black', borderRadius: 16, minHeight: 400, padding: 24, boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
          <div>
            <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20, color: 'var(--text-primary)' }}>BOQ–Material Mapping</h2>
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
              BOQ → Work Package → Materials → Request → Order → Use on site → QS Installation → BOQ Progress. Map once — everything after traces back.
            </p>
          </div>
          <div style={{ width: 320, maxWidth: '100%' }}>
            <Select label="Project" value={projectId} onChange={v => setProject(v)}
                    options={[{ value: '', label: '— Select a project —' },
                      ...projects.map(p => ({ value: p.project_id || p.id, label: `${p.project_code ? `${p.project_code} — ` : ''}${p.project_name}` }))]} />
          </div>
        </div>
        {project && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-base)', border: '1px solid var(--border-light)',
                        borderRadius: 10, padding: '8px 12px', marginBottom: 18 }}>
            <b style={{ color: 'var(--text-primary)' }}>{project.project_name}</b>
            {project.project_code && <> ({project.project_code})</>}{project.location && <> · {project.location}</>}
            {' · '}Principle: enter once → map once → flow through → report automatically. No double entry.
          </div>
        )}

        {needsProject && !inEditor ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🏗️</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>Select a project</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>Choose a project above, or open <b>MAP BOQs</b> to see every project's mapping coverage.</div>
          </div>
        ) : (
          <>
            {inEditor && <MMEditor key={boqItemId} boqItemId={boqItemId} materialId={searchParams.get('material')} {...shared} />}
            {shown === 'dashboard' && <MMDashboard {...shared} />}
            {shown === 'register' && <MMRegister {...shared} initialStatus={searchParams.get('status') || ''} mine={searchParams.get('mine') === '1'} />}
            {shown === 'unmapped' && <MMUnmapped {...shared} />}
            {shown === 'mapped' && <MMMapped {...shared} />}
            {shown === 'projects' && <MMProjects {...shared} onOpenProject={(id) => setProject(id, 'register')} />}
            {shown === 'site-measurement' && <MMSiteMeasurement {...shared} />}
          </>
        )}
      </div>
    </div>
  );
}
